import React from "react";
import { Box, CircularProgress, Tooltip, Typography } from "@mui/material";
import { getComplianceFrameworkBadgeText, getComplianceIcon } from "../../../assets/images/compliance/IconCompliance.jsx";
import { AH_BORDER, AH_MUTED, AH_TEXT, AH_TRACK, AH_WHITE, cardShellSx, tableShellSx } from "../AuditHub/theme";

/** Building blocks shared by the audit cards on the Controls page, on the Audit Hub tokens. */

export const PASSED_COLOR = "#a5d6a7";
export const FAILED_COLOR = "#ef9a9a";
export const PASSED_TEXT_COLOR = "#16a34a";
export const FAILED_TEXT_COLOR = "#dc2626";
export const EMPTY_TRACK = AH_BORDER;

export const labelSx = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: AH_MUTED,
  lineHeight: 1.3,
};

export const bigNumberSx = {
  fontSize: "1.6rem",
  fontWeight: 700,
  color: AH_TEXT,
  lineHeight: 1,
  fontVariantNumeric: "tabular-nums",
};

const IN_REVIEW_COLOR = "#fdba74";
const IN_REVIEW_TEXT = "#c2410c";
const NA_COLOR = "#94a3b8";
const MANUAL_PASSED_COLOR = "#15803d";
const MANUAL_FAILED_COLOR = "#b91c1c";

const RESULT_FIELDS = [
  { key: "autoPassed", label: "Automated passed", color: PASSED_COLOR },
  { key: "autoFailed", label: "Automated failed", color: FAILED_COLOR },
  { key: "manualPassed", label: "Manual passed", color: MANUAL_PASSED_COLOR },
  { key: "manualFailed", label: "Manual failed", color: MANUAL_FAILED_COLOR },
  { key: "notApplicable", label: "Not applicable", color: NA_COLOR },
  { key: "undecided", label: "Undecided", color: IN_REVIEW_COLOR },
];

const BAR_ORDER = ["autoPassed", "manualPassed", "notApplicable", "autoFailed", "manualFailed", "undecided"];

export const formatCount = (value) => (Number(value) || 0).toLocaleString();

export const CardShell = ({ children, sx }) => (
  <Box sx={{ ...tableShellSx, p: { xs: 1.5, sm: 2 }, display: "flex", flexDirection: "column", gap: 1.5, ...sx }}>{children}</Box>
);

export const CardHeader = ({ eyebrow, compliance, meta, action }) => {
  const iconSrc = getComplianceIcon(compliance);
  const badgeText = getComplianceFrameworkBadgeText(compliance);
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, rowGap: 1.25, flexWrap: "wrap" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0, flex: "1 1 320px" }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            backgroundColor: AH_WHITE,
            border: `1px solid ${AH_BORDER}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {iconSrc ? (
            <img src={iconSrc} alt="" style={{ maxWidth: 26, maxHeight: 26 }} />
          ) : (
            <Typography sx={{ fontWeight: 700, fontSize: 12, color: AH_TEXT }}>{badgeText || "—"}</Typography>
          )}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={labelSx}>{eyebrow}</Typography>
          <Typography title={compliance} sx={{ fontWeight: 700, fontSize: "1rem", color: AH_TEXT, lineHeight: 1.3, overflowWrap: "anywhere" }}>
            {compliance}
          </Typography>
          {meta ? <Box sx={{ mt: 0.25, fontSize: 13, color: AH_MUTED }}>{meta}</Box> : null}
        </Box>
      </Box>
      {action ? (
        <Box sx={{ display: "flex", justifyContent: { xs: "flex-start", sm: "flex-end" }, flex: { xs: "1 1 100%", sm: "0 1 380px" }, minWidth: 0 }}>
          {action}
        </Box>
      ) : null}
    </Box>
  );
};

export const Tile = ({ label, action, children, sx, align = "center" }) => (
  <Box sx={{ ...cardShellSx, px: 1.5, py: 1.25, display: "flex", flexDirection: "column", gap: 1, minWidth: 0, ...sx }}>
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, minHeight: 22 }}>
      <Typography noWrap sx={{ ...labelSx, minWidth: 0 }}>
        {label}
      </Typography>
      {action}
    </Box>
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: align === "start" ? "flex-start" : "center",
        gap: 1,
        minWidth: 0,
        minHeight: 0,
      }}
    >
      {children}
    </Box>
  </Box>
);

/** Three equal tiles on wide screens; the last tile spans the row on tablets; stacked on phones. */
export const tileGridSx = {
  display: "grid",
  gap: 1.25,
  gridTemplateColumns: {
    xs: "minmax(0, 1fr)",
    sm: "repeat(2, minmax(0, 1fr))",
    lg: "minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.3fr)",
  },
};
export const wideTileSx = { gridColumn: { sm: "1 / -1", lg: "auto" } };

const RING_SIZE = 72;

export const ScoreRing = ({ value, tooltip }) => {
  const hasValue = Number.isFinite(value);
  const clamped = hasValue ? Math.min(100, Math.max(0, value)) : 0;
  return (
    <Tooltip title={tooltip || ""} arrow placement="top">
      <Box sx={{ position: "relative", width: RING_SIZE, height: RING_SIZE, flexShrink: 0 }}>
        <CircularProgress
          variant="determinate"
          value={100}
          size={RING_SIZE}
          thickness={3.5}
          sx={{ color: hasValue ? FAILED_COLOR : EMPTY_TRACK, position: "absolute", inset: 0 }}
        />
        <CircularProgress
          variant="determinate"
          value={clamped}
          size={RING_SIZE}
          thickness={3.5}
          sx={{ color: PASSED_COLOR, position: "absolute", inset: 0 }}
        />
        <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: AH_TEXT, lineHeight: 1 }}>
            {hasValue ? `${clamped.toFixed(2)}%` : "—"}
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  );
};

const formatPercent = (value) => (Number.isFinite(value) ? `${value.toFixed(2)}%` : "—");

const scoreComparison = (score, baseline) => {
  if (!Number.isFinite(score) || !Number.isFinite(baseline)) return null;
  const delta = score - baseline;
  if (Math.abs(delta) < 0.005) return { color: AH_MUTED, text: "Same as the automated baseline" };
  const above = delta > 0;
  return {
    color: above ? PASSED_TEXT_COLOR : FAILED_TEXT_COLOR,
    text: `${Math.abs(delta).toFixed(2)}% ${above ? "above" : "below"} the automated baseline`,
  };
};

const QuietLine = ({ children }) => (
  <Typography sx={{ fontSize: 13, color: AH_MUTED, overflowWrap: "anywhere" }}>{children}</Typography>
);

/** Segmented bar. `scale` keeps a remainder on the track; otherwise the segments fill the bar. */
export const StackedBar = ({ segments, scale, height = 8, tooltip }) => {
  const sum = segments.reduce((total, segment) => total + (Number(segment.value) || 0), 0);
  const total = Math.max(Number(scale) || 0, sum);
  const bar = (
      <Box
        sx={{ height, width: "100%", borderRadius: 99, overflow: "hidden", display: "flex", bgcolor: AH_TRACK }}
        role="img"
        aria-label={tooltip || ""}
      >
      {total > 0
        ? segments.map((segment) =>
            segment.value > 0 ? (
              <Box key={segment.key} sx={{ width: `${total > 0 ? (segment.value / total) * 100 : 0}%`, bgcolor: segment.color }} />
            ) : null
          )
        : null}
    </Box>
  );
  if (!tooltip) return bar;
  return (
    <Tooltip title={tooltip} arrow placement="top">
      {bar}
    </Tooltip>
  );
};

/** Manual-control progress: decided, then in review, with outstanding left on the track. */
export const ReviewTrack = ({ decided, inReview, outstanding, total, height = 8 }) => {
  const decidedN = Number(decided) || 0;
  const reviewN = Number(inReview) || 0;
  const totalN = Number(total) || 0;
  const openN = outstanding == null ? Math.max(0, totalN - decidedN - reviewN) : Number(outstanding) || 0;
  const scale = Math.max(totalN, decidedN + reviewN);
  const tip = scale > 0 ? `${formatCount(decidedN)} decided · ${formatCount(reviewN)} in review · ${formatCount(openN)} outstanding` : "No manual controls";
  return (
    <StackedBar
      height={height}
      scale={scale}
      tooltip={tip}
      segments={[
        { key: "decided", value: decidedN, color: PASSED_COLOR },
        { key: "inReview", value: reviewN, color: IN_REVIEW_COLOR },
      ]}
    />
  );
};

/**
 * Readiness score against the automated baseline.
 * `coverage` is optional text such as "1 of 2 accounts scored".
 */
export const ReadinessBody = ({ score, baseline, coverage, pendingLabel, tooltip }) => {
  const comparison = scoreComparison(score, baseline);
  const pending = !Number.isFinite(score) && !Number.isFinite(baseline);
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <ScoreRing value={score} tooltip={tooltip} />
      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 0.25 }}>
        {pending ? (
          <QuietLine>{pendingLabel || "—"}</QuietLine>
        ) : (
          <>
            <Tooltip title="Automated score before manual pass and fail decisions." arrow placement="top">
              <Typography sx={{ fontSize: 12, color: AH_MUTED, width: "fit-content" }}>Automated baseline</Typography>
            </Tooltip>
            <Typography sx={{ fontSize: "1.05rem", fontWeight: 700, color: AH_TEXT, lineHeight: 1.2, fontVariantNumeric: "tabular-nums" }}>
              {formatPercent(baseline)}
            </Typography>
            {comparison ? (
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: comparison.color, overflowWrap: "anywhere" }}>
                {comparison.text}
              </Typography>
            ) : null}
          </>
        )}
        {coverage ? <QuietLine>{coverage}</QuietLine> : null}
      </Box>
    </Box>
  );
};

const ReviewStat = ({ color, value, label }) => (
  <Typography sx={{ fontSize: 12, color: AH_MUTED, whiteSpace: "nowrap" }}>
    <Box component="span" sx={{ fontWeight: 700, color, mr: 0.5, fontVariantNumeric: "tabular-nums" }}>
      {value}
    </Box>
    {label}
  </Typography>
);

/** Manual controls decided, in review, and still outstanding. */
export const ManualReviewBody = ({ progress, known, unavailable }) => {
  if (!known) return <QuietLine>{unavailable || "—"}</QuietLine>;
  if (!progress || progress.total <= 0) return <QuietLine>No manual controls</QuietLine>;
  const percent = Math.round((progress.decided / progress.total) * 100);
  return (
    <>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
        <Typography sx={bigNumberSx}>{formatCount(progress.decided)}</Typography>
        <Typography sx={{ fontSize: 13, color: AH_MUTED }}>of {formatCount(progress.total)} decided</Typography>
        <Typography sx={{ ml: "auto", fontSize: 13, fontWeight: 700, color: AH_TEXT, fontVariantNumeric: "tabular-nums" }}>
          {percent}%
        </Typography>
      </Box>
      <ReviewTrack
        decided={progress.decided}
        inReview={progress.inReview}
        outstanding={progress.outstanding}
        total={progress.total}
      />
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
        <ReviewStat color={IN_REVIEW_TEXT} value={formatCount(progress.inReview)} label="in review" />
        <ReviewStat color={AH_MUTED} value={formatCount(progress.outstanding)} label="outstanding" />
      </Box>
    </>
  );
};

/** Automated and manual control counts that make up one scope or the whole assessment. */
export const ControlResultsBody = ({ components, known, unavailable, pendingLabel }) => {
  if (!known) return <QuietLine>{unavailable || "—"}</QuietLine>;
  const total = components
    ? RESULT_FIELDS.reduce((sum, field) => sum + (Number(components[field.key]) || 0), 0)
    : 0;
  if (total <= 0) return <QuietLine>{pendingLabel || "No control results yet"}</QuietLine>;
  const byKey = new Map(RESULT_FIELDS.map((field) => [field.key, field]));
  const excluded = (components.notApplicable || 0) + (components.undecided || 0);
  return (
    <>
      <StackedBar
        segments={BAR_ORDER.map((key) => ({ key, value: components[key] || 0, color: byKey.get(key).color }))}
        tooltip={RESULT_FIELDS.map((field) => `${field.label}: ${formatCount(components[field.key])}`).join(" · ")}
      />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          columnGap: 1.5,
          rowGap: 0.5,
        }}
      >
        {RESULT_FIELDS.map((field) => (
          <Box key={field.key} sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: field.color, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: AH_TEXT, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>
              {formatCount(components[field.key])}
            </Typography>
            <Typography sx={{ fontSize: 12, color: AH_MUTED, minWidth: 0, overflowWrap: "anywhere" }}>{field.label}</Typography>
          </Box>
        ))}
      </Box>
      {excluded > 0 ? (
        <QuietLine>Not applicable and undecided controls are excluded from the readiness score.</QuietLine>
      ) : null}
    </>
  );
};
